import React, { memo } from "react";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard,
  Users,
  ShoppingCart,
  CreditCard,
  Receipt,
  Bell,
  FileBarChart,
  UserCog,
  Settings,
  LogOut,
} from "lucide-react";

interface CustomerPaymentSidebarProps {
  user: any;
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (open: boolean) => void;
  onClose: () => void;
  onNavigate?: (page: "dashboard" | "customers" | "sales" | "payments" | "expenses" | "notifications" | "reports" | "users" | "settings") => void;
}

export const CustomerPaymentSidebar = memo(function CustomerPaymentSidebar({
  user,
  mobileMenuOpen,
  setMobileMenuOpen,
  onClose,
  onNavigate,
}: CustomerPaymentSidebarProps) {
  const handleNav = (page: "dashboard" | "customers" | "sales" | "payments" | "expenses" | "notifications" | "reports" | "users" | "settings") => {
    onClose();
    onNavigate?.(page);
    setMobileMenuOpen(false);
  };

  return (
    <>
      {/* Mobile Menu Overlay */}
      <div
        className={`md:hidden fixed inset-0 top-14 z-40 transition-all duration-300 ease-out ${
          mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div
          className={`absolute inset-0 bg-background/80 backdrop-blur-sm transition-opacity duration-300 ${
            mobileMenuOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={() => setMobileMenuOpen(false)}
        />
        <div
          className={`absolute top-0 right-0 h-full w-72 bg-card border-l shadow-2xl transform transition-transform duration-300 ease-out overflow-y-auto ${
            mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          onClick={(e) => e.stopPropagation()}
          data-scrollable="true"
          style={{ overscrollBehavior: 'contain' }}
        >
          <div className="h-20 px-4 border-b bg-gradient-to-r from-primary/5 to-primary/10">
            <div className="flex items-center gap-3 h-full">
              <div className="h-12 w-12 rounded-full bg-primary flex items-center justify-center shadow-lg ring-2 ring-primary/20">
                <span className="text-primary-foreground font-bold text-lg">
                  {user?.fullName?.charAt(0)?.toUpperCase() || 'ق'}
                </span>
              </div>
              <div className="flex flex-col min-w-0 flex-1">
                <span className="font-bold text-sm truncate">{user?.fullName || 'بەکارهێنەر'}</span>
                <span className="text-xs text-muted-foreground truncate">{user?.email || ''}</span>
                <span className="text-xs font-medium text-primary">
                  {user?.role === 'admin' ? 'بەڕێوەبەر' : 'بەکارهێنەر'}
                </span>
              </div>
            </div>
          </div>
          <nav className="p-4 space-y-2">
            <button
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 text-muted-foreground hover:bg-muted"
              onClick={() => handleNav("dashboard")}
            >
              <LayoutDashboard className="h-5 w-5" />
              داشبۆرد
            </button>
            <button
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 text-muted-foreground hover:bg-muted"
              onClick={() => handleNav("customers")}
            >
              <Users className="h-5 w-5" />
              کڕیارەکان
            </button>
            <button
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 text-muted-foreground hover:bg-muted"
              onClick={() => handleNav("sales")}
            >
              <ShoppingCart className="h-5 w-5" />
              فرۆشتنەکان
            </button>
            <button
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 bg-primary/10 text-primary"
              onClick={() => setMobileMenuOpen(false)}
            >
              <CreditCard className="h-5 w-5" />
              پارەدان
            </button>
            <button
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 text-muted-foreground hover:bg-muted"
              onClick={() => handleNav("expenses")}
            >
              <Receipt className="h-5 w-5" />
              خەرجییەکان
            </button>
            <button
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 text-muted-foreground hover:bg-muted"
              onClick={() => handleNav("notifications")}
            >
              <Bell className="h-5 w-5" />
              دواکەوتووەکان
            </button>
            <button
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 text-muted-foreground hover:bg-muted"
              onClick={() => handleNav("reports")}
            >
              <FileBarChart className="h-5 w-5" />
              ڕاپۆرتەکان
            </button>
            <div className="border-t my-2" />
            <button
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 text-muted-foreground hover:bg-muted"
              onClick={() => handleNav("users")}
            >
              <UserCog className="h-5 w-5" />
              بەکارهێنەران
            </button>
            <button
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted transition-all duration-200"
              onClick={() => handleNav("settings")}
            >
              <Settings className="h-5 w-5" />
              ڕێکخستن
            </button>
            <button
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted transition-all duration-200"
              onClick={() => { onClose(); setMobileMenuOpen(false); }}
            >
              <LogOut className="h-5 w-5" />
              چوونەدەرەوە
            </button>
          </nav>
        </div>
      </div>

      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-56 border-l bg-white dark:bg-muted/30 flex-col h-screen">
        <div className="h-20 px-4 border-b bg-gradient-to-r from-primary/5 to-primary/10">
          <div className="flex items-center gap-3 h-full">
            <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center shadow-lg ring-2 ring-primary/20">
              <span className="text-primary-foreground font-bold text-base">
                {user?.fullName?.charAt(0)?.toUpperCase() || 'ق'}
              </span>
            </div>
            <div className="flex flex-col min-w-0 flex-1">
              <span className="font-bold text-sm truncate">{user?.fullName || 'بەکارهێنەر'}</span>
              <span className="text-[10px] text-muted-foreground truncate leading-tight">{user?.email || ''}</span>
              <span className="text-xs font-medium text-primary">
                {user?.role === 'admin' ? 'بەڕێوەبەر' : 'بەکارهێنەر'}
              </span>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          <button onClick={() => { onClose(); onNavigate?.("dashboard"); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-muted cursor-pointer">
            <LayoutDashboard className="h-4 w-4" />
            داشبۆرد
          </button>
          <button onClick={() => { onClose(); onNavigate?.("customers"); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-muted cursor-pointer">
            <Users className="h-4 w-4" />
            کڕیارەکان
          </button>
          <button onClick={() => { onClose(); onNavigate?.("sales"); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-muted cursor-pointer">
            <ShoppingCart className="h-4 w-4" />
            فرۆشتنەکان
          </button>
          <button className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-primary/10 text-primary cursor-pointer">
            <CreditCard className="h-4 w-4" />
            پارەدان
          </button>
          <button onClick={() => { onClose(); onNavigate?.("expenses"); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-muted cursor-pointer">
            <Receipt className="h-4 w-4" />
            خەرجییەکان
          </button>
          <button onClick={() => { onClose(); onNavigate?.("notifications"); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-muted cursor-pointer">
            <Bell className="h-4 w-4" />
            دواکەوتووەکان
          </button>
          <button onClick={() => { onClose(); onNavigate?.("reports"); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-muted cursor-pointer">
            <FileBarChart className="h-4 w-4" />
            ڕاپۆرتەکان
          </button>
          <button onClick={() => { onClose(); onNavigate?.("users"); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-muted cursor-pointer">
            <UserCog className="h-4 w-4" />
            بەکارهێنەران
          </button>
          <button onClick={() => { onClose(); onNavigate?.("settings"); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-muted cursor-pointer">
            <Settings className="h-4 w-4" />
            ڕێکخستن
          </button>
        </nav>
        <div className="p-3 border-t">
          <button onClick={onClose} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-muted cursor-pointer">
            <LogOut className="h-4 w-4" />
            چوونەدەرەوە
          </button>
        </div>
      </aside>
    </>
  );
});
